import { FC } from "react";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useCommands } from "@components/commands/useCommands";
import { CommandsDirectory } from "@components/commands/lib";
import { useSettings } from "@components/settings/SettingsProvider";
import {
    useResolvedNavigationShortcuts,
    ResolvedNavigationShortcut,
} from "./useResolvedNavigationShortcuts";

interface ShortcutKeyboardBindingProps {
    shortcut: ResolvedNavigationShortcut;
    keys: string;
}

const ShortcutKeyboardBinding: FC<ShortcutKeyboardBindingProps> = ({
    shortcut,
    keys,
}) => {
    const navigate = useStateAwareNavigate();
    const { open: openSettings } = useSettings();

    useKeyboardCommand({
        keys,
        callback: () => {
            if (shortcut.action === "settings") {
                openSettings();
            } else {
                navigate(shortcut.path);
            }
        },
        options: {
            description: shortcut.description,
            useKey: true,
            enabled: shortcut.isEnabled,
        },
    });

    return null;
};

interface WorkspaceSwitcherBindingProps {
    hasMultipleWorkspaces: boolean;
}

const WorkspaceSwitcherBinding: FC<WorkspaceSwitcherBindingProps> = ({
    hasMultipleWorkspaces,
}) => {
    const { open: openCommands } = useCommands();

    useKeyboardCommand({
        keys: "o>w",
        callback: () => {
            openCommands(CommandsDirectory.WORKSPACES);
        },
        options: {
            description: "Open workspace switcher",
            preventDefault: true,
            enabled: hasMultipleWorkspaces,
            keyup: true,
        },
    });

    return null;
};

interface NavigationShortcutRegistrarProps {
    hasMultipleWorkspaces: boolean;
}

export const NavigationShortcutRegistrar: FC<
    NavigationShortcutRegistrarProps
> = ({ hasMultipleWorkspaces }) => {
    const resolvedShortcuts = useResolvedNavigationShortcuts();

    return (
        <>
            <WorkspaceSwitcherBinding
                hasMultipleWorkspaces={hasMultipleWorkspaces}
            />
            {resolvedShortcuts.map((shortcut) =>
                shortcut.keys ? (
                    <ShortcutKeyboardBinding
                        key={shortcut.id}
                        shortcut={shortcut}
                        keys={shortcut.keys}
                    />
                ) : null,
            )}
        </>
    );
};
